import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useAdminModelOverrides,
  useDeleteModelOverride,
  useUpsertModelOverride,
  type ModelOverrideActionType,
} from "@/hooks/queries/admin/useAdminModelOverrides";

const ACTION_TYPES: ModelOverrideActionType[] = [
  "ranking",
  "interview_prep",
  "ai_evaluation",
  "interview_analysis",
  // Phase 2.7 (2026-05-15) — resume_format admits Opus uplift on top of
  // the Haiku default. Backed by DB CHECK + Pydantic Literal landed in
  // the same PR (migration 0061 + schemas/admin/model_overrides.py).
  "resume_format",
];

// Per-action allowed model picker. Mirrors backend
// app/ai/config.py::ALLOWED_MODELS — judgment actions admit Sonnet+Opus,
// resume_format admits Haiku+Opus (Phase 2.7).
const MODEL_CHOICES_BY_ACTION: Record<ModelOverrideActionType, string[]> = {
  ranking: ["claude-sonnet-4-6", "claude-opus-4-6"],
  interview_prep: ["claude-sonnet-4-6", "claude-opus-4-6"],
  ai_evaluation: ["claude-sonnet-4-6", "claude-opus-4-6"],
  interview_analysis: ["claude-sonnet-4-6", "claude-opus-4-6"],
  resume_format: ["claude-haiku-4-5", "claude-opus-4-6"],
};

function defaultModelFor(action: ModelOverrideActionType): string {
  // First entry of MODEL_CHOICES_BY_ACTION is the action's default model.
  // tsconfig.json has noUncheckedIndexedAccess; the choice list is a
  // typed Record with non-empty arrays for every action, so the bracket
  // access is guaranteed safe — the ?? "" fallback is a defensive no-op.
  return MODEL_CHOICES_BY_ACTION[action][0] ?? "";
}

export default function AdminModelOverrides(): JSX.Element {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data, isLoading, error } = useAdminModelOverrides(tenantId);
  const upsert = useUpsertModelOverride(tenantId);
  const del = useDeleteModelOverride(tenantId);

  const [actionType, setActionType] =
    useState<ModelOverrideActionType>("ranking");
  const [modelName, setModelName] = useState("claude-opus-4-6");
  const [reason, setReason] = useState("");

  // Snap the model picker to the first valid option for the action when
  // the action changes (e.g. switching to resume_format must invalidate
  // claude-sonnet-4-6 because it's not in resume_format's allowlist).
  useEffect(() => {
    const choices = MODEL_CHOICES_BY_ACTION[actionType];
    if (!choices.includes(modelName)) {
      // Prefer Opus uplift when switching to an action whose allowlist
      // includes Opus (it's the universal override target). Falls back
      // to the action's default model otherwise.
      const opus = choices.find((m) => m === "claude-opus-4-6");
      setModelName(opus ?? defaultModelFor(actionType));
    }
  }, [actionType, modelName]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    upsert.mutate({ action_type: actionType, model_name: modelName, reason });
  }

  if (isLoading)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Loading…
      </div>
    );
  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        Couldn't load overrides: {error.message}
      </div>
    );
  }

  const overrides = data?.overrides ?? [];

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/tenants/${tenantId}`}
          className="text-sm text-accent-primary-hover hover:underline dark:text-accent-secondary"
        >
          ← Tenant
        </Link>
      </div>
      <h1 className="text-xl font-semibold text-text-primary dark:text-text-primary">
        Model overrides
      </h1>

      <div className="overflow-hidden rounded-lg border border-brand-100 bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
        <table className="w-full text-sm">
          <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
            <tr>
              <th className="px-4 py-2 font-medium">Action</th>
              <th className="px-4 py-2 font-medium">Model</th>
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 font-medium">Updated</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {overrides.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  No overrides set.
                </td>
              </tr>
            ) : (
              overrides.map((o) => (
                <tr
                  key={o.action_type}
                  className="border-t border-brand-100 dark:border-white/[0.04]"
                >
                  <td className="px-4 py-2 text-text-primary dark:text-text-primary">
                    {o.action_type}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {o.model_name}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {o.reason}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {new Date(o.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => del.mutate(o.action_type)}
                      disabled={del.isPending}
                      className="text-sm text-red-600 hover:underline disabled:opacity-50 dark:text-red-400"
                    >
                      Clear
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-3 rounded-lg border border-brand-100 bg-white p-4 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark"
      >
        <h2 className="text-sm font-semibold text-text-primary dark:text-text-primary">
          Set / update override
        </h2>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary dark:text-brand-100/70">
              Action
            </label>
            <select
              value={actionType}
              onChange={(e) =>
                setActionType(e.target.value as ModelOverrideActionType)
              }
              className="w-full rounded border border-brand-200 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary"
            >
              {ACTION_TYPES.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary dark:text-brand-100/70">
              Model
            </label>
            <select
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              className="w-full rounded border border-brand-200 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary"
            >
              {MODEL_CHOICES_BY_ACTION[actionType].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary dark:text-brand-100/70">
              Reason
            </label>
            <input
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded border border-brand-200 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary"
            />
          </div>
        </div>
        {upsert.isError ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {upsert.error.message}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={upsert.isPending}
          className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
        >
          {upsert.isPending ? "Saving…" : "Save override"}
        </button>
      </form>
    </div>
  );
}
